import { msg } from "@lingui/core/macro";
import { createMemo, createSignal, For, Show } from "solid-js";
import { Link, useNavigate, useSearch } from "@tanstack/solid-router";
import {
  Ban,
  Mail,
  RotateCcw,
  Search,
  ShieldCheck,
  Trash2,
  // ── F3-J: student import from Excel (wizard's first embedded use) ─────────
  Upload,
  Users,
} from "@fleet/icons";
import { Avatar } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Badge, Card, CardContent, Label, Skeleton, TextField } from "@fleet/ui";
import { Checkbox } from "@/components/ui/checkbox";
import { Select } from "@/components/ui/select";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import { Dialog, DialogDescription, DialogTitle } from "@/components/ui/dialog";
import { showToast } from "@/components/ui/toast";
import { api, createApiMutation, createApiQuery } from "@/lib/api";
import {
  ADMIN_ROLES,
  adminQueryKeys,
  type AdminUserDTO,
  assignableRolesFor,
  canAdministerUser,
  type components,
  filterAdminUsers,
} from "@space/shared";
import { useI18n } from "@/providers/i18n-provider";
import { useAdminCapabilities } from "../use-admin-capabilities";
import { useActiveOrg } from "@/features/academy/hooks/use-active-org";
import { ImportWizard } from "@/features/io/components/import-wizard";
import { ioQueryKeys } from "@/features/io/api";
// ── fin F3-J ──

type ErrorModel = components["schemas"]["ErrorModel"];
type UserListResponse = components["schemas"]["DirectoryUserListResponse"];

/** Action the URL is asking us to perform on a user (`?user=<id>&action=ban`). */
type UserAction = "ban" | "remove";

export function UserManager() {
  const { t } = useI18n();
  const { can, role: actorRole } = useAdminCapabilities();
  const search = useSearch({ strict: false });
  const navigate = useNavigate();

  const [banReason, setBanReason] = createSignal("");
  // F2-C: bulk selection (component-local, not URL-addressable).
  const [selected, setSelected] = createSignal<Set<string>>(new Set());
  const [bulkRole, setBulkRole] = createSignal<string>("user");

  // ── F3-J: student import wizard (io.students) over the active org ─────────
  const { activeOrgId } = useActiveOrg();
  const [importOpen, setImportOpen] = createSignal(false);
  const ioEntitiesQuery = createApiQuery<components["schemas"]["IoEntitiesResponse"], ErrorModel>(
    () => ioQueryKeys.entities(activeOrgId()),
    async () => api.GET("/api/io/entities", { params: { query: { orgId: activeOrgId() } } }),
    () => ({ staleTime: 60_000 }),
  );
  const studentsEntity = createMemo(() =>
    (ioEntitiesQuery.data?.data ?? []).find((e) => e.key === "io.students" && e.importable),
  );
  // ── fin F3-J ──

  // F2-C: filters go SERVER-SIDE (role/org/createdAfter/search). The directory
  // endpoint returns already-filtered rows; we still paginate client-side.
  const usersQuery = createApiQuery<UserListResponse, ErrorModel>(
    () => [
      ...adminQueryKeys.users({ q: search().q, role: search().role }),
      search().org ?? "",
      search().createdAfter ?? "",
    ],
    async () =>
      api.GET("/api/admin/users", {
        params: {
          query: {
            search: search().q,
            role: search().role,
            org: search().org,
            createdAfter: search().createdAfter,
          },
        },
      }),
    () => ({ staleTime: 15_000 }),
  );

  const users = createMemo<AdminUserDTO[]>(() => usersQuery.data?.data ?? []);

  const setRoleMutation = createApiMutation(async (vars: { user_id: string; role: string }) =>
    api.POST("/api/admin/set-role", { body: vars }),
  );
  const banMutation = createApiMutation(async (vars: { user_id: string; reason?: string }) =>
    api.POST("/api/admin/ban-user", { body: vars }),
  );
  const unbanMutation = createApiMutation(async (vars: { userId: string }) =>
    api.POST("/api/admin/unban-user", { body: vars }),
  );
  const revokeMutation = createApiMutation(async (vars: { userId: string }) =>
    api.POST("/api/admin/revoke-sessions", { body: vars }),
  );
  const removeMutation = createApiMutation(async (vars: { userId: string }) =>
    api.POST("/api/admin/remove-user", { body: vars }),
  );
  const bulkMutation = createApiMutation(
    async (vars: { action: "suspend" | "unsuspend" | "set_role"; ids: string[]; role?: string }) =>
      api.POST("/api/admin/users/bulk", { body: vars }),
  );

  // Client pagination only (server already applied q/role/org/createdAfter).
  const result = createMemo(() => filterAdminUsers(users(), { page: search().page }));

  /** Patch the URL search params (keeps list state addressable, #15b). */
  const patchSearch = (patch: Record<string, unknown>) => {
    void navigate({
      to: ".",
      search: (prev) => {
        const next: Record<string, unknown> = { ...(prev as Record<string, unknown>), ...patch };
        for (const key of Object.keys(next)) {
          if (next[key] === undefined || next[key] === "") {
            delete next[key];
          }
        }
        return next;
      },
      replace: true,
    });
  };

  const dialogUser = createMemo<AdminUserDTO | undefined>(() => {
    const id = search().user;
    return id ? users().find((u) => u.id === id) : undefined;
  });
  const dialogAction = (): UserAction | undefined =>
    // oxlint-disable-next-line typescript/no-unsafe-type-assertion
    search().action as UserAction | undefined;

  const closeDialog = () => {
    setBanReason("");
    patchSearch({ user: undefined, action: undefined });
  };

  const openDialog = (userId: string, action: UserAction) => {
    patchSearch({ user: userId, action });
  };

  const afterMutation = (message: string) => {
    void usersQuery.refetch();
    showToast(message, "success");
  };

  // The table's FILTER offers ALL roles (searching by role isn't
  // granting a role); the per-user SELECTOR only offers the ones this actor
  // can grant. They used to be the same full list, so the UI invited
  // an escalation the backend now rejects with 403.
  const filterRoleOptions = createMemo(() => ADMIN_ROLES.map((r) => ({ value: r, label: r })));
  const grantableRoleOptions = createMemo(() =>
    assignableRolesFor(actorRole()).map((r) => ({ value: r, label: r })),
  );

  // ── Selection helpers ──
  const toggle = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };
  const allOnPageSelected = createMemo(
    () => result().rows.length > 0 && result().rows.every((u) => selected().has(u.id)),
  );
  const toggleAll = () => {
    setSelected((prev) => {
      const next = new Set(prev);
      const { rows } = result();
      if (rows.every((u) => next.has(u.id))) {
        for (const u of rows) {
          next.delete(u.id);
        }
      } else {
        for (const u of rows) {
          next.add(u.id);
        }
      }
      return next;
    });
  };
  const clearSelection = () => setSelected(new Set<string>());

  const runBulk = (action: "suspend" | "unsuspend" | "set_role") => {
    const ids = [...selected()];
    if (ids.length === 0) {
      return;
    }
    bulkMutation.mutate(
      { action, ids, role: action === "set_role" ? bulkRole() : undefined },
      {
        onSuccess: (res) => {
          clearSelection();
          const { affected } = res;
          afterMutation(t(msg`${affected} users updated`));
        },
        onError: () => showToast(t(msg`Bulk action failed`), "error"),
      },
    );
  };

  const handleBan = () => {
    const user = dialogUser();
    if (!user) {
      closeDialog();
      return;
    }
    banMutation.mutate(
      { user_id: user.id, reason: banReason().trim() || undefined },
      {
        onSuccess: () => {
          afterMutation(t(msg`User banned`));
          closeDialog();
        },
      },
    );
  };

  const handleRemove = () => {
    const user = dialogUser();
    if (!user) {
      closeDialog();
      return;
    }
    removeMutation.mutate(
      { userId: user.id },
      {
        onSuccess: () => {
          afterMutation(t(msg`User removed`));
          closeDialog();
        },
      },
    );
  };

  return (
    <div class="space-y-6">
      <div class="flex items-center justify-between gap-3 flex-wrap">
        <div class="flex items-center gap-3">
          <Users class="h-8 w-8 text-primary" />
          <div>
            <h2 class="text-3xl font-bold tracking-tight">{t(msg`Users`)}</h2>
            <p class="text-sm text-muted-foreground">
              {(() => {
                const { total } = result();
                return t(msg`${total} users match`);
              })()}
            </p>
          </div>
        </div>
        <div class="flex items-center gap-2">
          {/* ── F3-J: import de alumnos (Excel) — primer uso embebido ── */}
          <Show when={studentsEntity()}>
            <Button
              variant="outline"
              onClick={() => setImportOpen(true)}
              data-testid="io-import-students-toolbar"
            >
              <Upload class="h-4 w-4" />
              {t(msg`Import students`)}
            </Button>
          </Show>
          {/* ── fin F3-J ── */}
          <Show when={can("users.invite")}>
            <Link
              to="/admin/invites"
              class="inline-flex items-center gap-1.5 rounded-lg border border-border px-3 py-2 text-sm font-medium hover:bg-accent/10"
            >
              <Mail class="h-4 w-4" />
              {t(msg`Invitations`)}
            </Link>
          </Show>
        </div>
      </div>

      {/* ── F3-J: wizard montado bajo demanda ── */}
      <Show when={studentsEntity()}>
        {(entity) => (
          <ImportWizard
            entity={entity()}
            orgId={activeOrgId()}
            open={importOpen()}
            onOpenChange={setImportOpen}
            onDone={() => void usersQuery.refetch()}
          />
        )}
      </Show>
      {/* ── fin F3-J ── */}

      <Card class="bg-primary/5 border-primary/10">
        <CardContent class="p-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:flex-wrap">
          <div class="relative flex-1 min-w-[12rem]">
            <Search class="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <TextField
              placeholder={t(msg`Search by name, email or cédula…`)}
              class="pl-9"
              value={search().q ?? ""}
              onInput={(e) => {
                patchSearch({ q: e.currentTarget.value, page: undefined });
              }}
            />
          </div>
          <div class="w-full sm:w-44">
            <Select<string>
              aria-label={t(msg`Filter by role`)}
              placeholder={t(msg`All roles`)}
              value={search().role ?? ""}
              options={[{ value: "", label: t(msg`All roles`) }, ...filterRoleOptions()]}
              onChange={(v) => {
                patchSearch({ role: v || undefined, page: undefined });
              }}
            />
          </div>
          <div class="w-full sm:w-44">
            <TextField
              type="date"
              aria-label={t(msg`Created after`)}
              value={search().createdAfter ?? ""}
              onInput={(e) => {
                patchSearch({ createdAfter: e.currentTarget.value || undefined, page: undefined });
              }}
            />
          </div>
        </CardContent>
      </Card>

      {/* F2-C: bulk action bar (appears when rows are selected). */}
      <Show when={selected().size > 0}>
        <Card class="bg-accent/10 border-accent/20 sticky top-2 z-10" data-testid="bulk-bar">
          <CardContent class="p-3 flex flex-wrap items-center gap-3">
            <span class="text-sm font-medium">
              {(() => {
                const count = selected().size;
                return t(msg`${count} selected`);
              })()}
            </span>
            <div class="flex-1" />
            <Show when={can("users.bulk")}>
              <Button variant="outline" size="sm" onClick={() => runBulk("suspend")}>
                <Ban class="mr-1.5 h-4 w-4" />
                {t(msg`Suspend`)}
              </Button>
              <Button variant="outline" size="sm" onClick={() => runBulk("unsuspend")}>
                <ShieldCheck class="mr-1.5 h-4 w-4" />
                {t(msg`Reactivate`)}
              </Button>
            </Show>
            <Show when={can("users.setRole")}>
              <div class="w-32">
                <Select<string>
                  aria-label={t(msg`Bulk role`)}
                  value={bulkRole()}
                  options={grantableRoleOptions()}
                  onChange={(v) => setBulkRole(v ?? "user")}
                />
              </div>
              <Button variant="outline" size="sm" onClick={() => runBulk("set_role")}>
                {t(msg`Set role`)}
              </Button>
            </Show>
            <Button variant="ghost" size="sm" onClick={clearSelection}>
              {t(msg`Clear`)}
            </Button>
          </CardContent>
        </Card>
      </Show>

      <Show
        when={!usersQuery.isLoading}
        fallback={
          <div class="space-y-3">
            <For each={Array.from({ length: 6 }, () => 0)}>
              {() => <Skeleton class="h-20 w-full rounded-xl" />}
            </For>
          </div>
        }
      >
        <Show when={result().rows.length > 0}>
          <div class="flex items-center gap-2 px-1">
            <Checkbox
              checked={allOnPageSelected()}
              onChange={toggleAll}
              aria-label={t(msg`Select all`)}
            />
            <span class="text-xs text-muted-foreground">{t(msg`Select all on page`)}</span>
          </div>
        </Show>

        <div class="space-y-3">
          <For
            each={result().rows}
            fallback={
              <Card>
                <CardContent class="p-8 text-center text-sm text-muted-foreground">
                  {t(msg`No users found.`)}
                </CardContent>
              </Card>
            }
          >
            {(user) => (
              <Card>
                <CardContent class="p-4 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                  <div class="min-w-0 flex items-center gap-3">
                    <Checkbox
                      checked={selected().has(user.id)}
                      onChange={() => toggle(user.id)}
                      aria-label={t(msg`Select user`)}
                    />
                    {/* PLATFORM back-office: the photo is NOT redacted here.
                        No need to: rule zero prevents a minor's photo from
                        even reaching the bucket, so there's nothing to
                        hide from whoever administers the installation. */}
                    <Avatar
                      userId={user.id}
                      name={user.name}
                      email={user.email}
                      image={user.image}
                      testId="admin-user-avatar"
                    />
                    <div class="min-w-0">
                      <Link
                        to="/admin/users/$userId"
                        params={{ userId: user.id }}
                        class="font-bold truncate flex items-center gap-2 hover:underline"
                      >
                        {user.name || t(msg`(no name)`)}
                        <Show when={user.banned}>
                          <Badge variant="destructive">{t(msg`Banned`)}</Badge>
                        </Show>
                      </Link>
                      <div class="text-xs text-muted-foreground truncate">
                        {user.email}
                        <Show when={user.nationalId}> · {user.nationalId}</Show>
                      </div>
                    </div>
                  </div>

                  <div class="flex flex-wrap items-center gap-2">
                    <Show
                      when={can("users.setRole") && canAdministerUser(actorRole(), user.role)}
                      fallback={
                        <Badge variant="secondary" class="capitalize">
                          {user.role}
                        </Badge>
                      }
                    >
                      <div class="w-36">
                        <Select<string>
                          aria-label={t(msg`Role`)}
                          value={user.role}
                          options={grantableRoleOptions()}
                          onChange={(role) => {
                            setRoleMutation.mutate(
                              { user_id: user.id, role },
                              { onSuccess: () => afterMutation(t(msg`Role updated`)) },
                            );
                          }}
                        />
                      </div>
                    </Show>

                    <Show when={can("users.ban")}>
                      <Show
                        when={user.banned}
                        fallback={
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openDialog(user.id, "ban")}
                          >
                            <Ban class="mr-1.5 h-4 w-4" />
                            {t(msg`Ban`)}
                          </Button>
                        }
                      >
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            unbanMutation.mutate(
                              { userId: user.id },
                              { onSuccess: () => afterMutation(t(msg`User unbanned`)) },
                            );
                          }}
                        >
                          <ShieldCheck class="mr-1.5 h-4 w-4" />
                          {t(msg`Unban`)}
                        </Button>
                      </Show>
                    </Show>

                    <Show when={can("users.revokeSessions")}>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          revokeMutation.mutate(
                            { userId: user.id },
                            { onSuccess: () => afterMutation(t(msg`Sessions revoked`)) },
                          );
                        }}
                      >
                        <RotateCcw class="mr-1.5 h-4 w-4" />
                        {t(msg`Revoke sessions`)}
                      </Button>
                    </Show>

                    <Show when={can("users.remove")}>
                      <Button
                        variant="ghost"
                        size="sm"
                        class="text-destructive hover:text-destructive"
                        onClick={() => openDialog(user.id, "remove")}
                      >
                        <Trash2 class="mr-1.5 h-4 w-4" />
                        {t(msg`Remove`)}
                      </Button>
                    </Show>
                  </div>
                </CardContent>
              </Card>
            )}
          </For>
        </div>

        <Show when={result().totalPages > 1}>
          <div class="flex items-center justify-center gap-4 pt-2">
            <Button
              variant="ghost"
              size="sm"
              disabled={result().page <= 1}
              onClick={() => patchSearch({ page: result().page - 1 })}
            >
              {t(msg`Previous`)}
            </Button>
            <span class="text-sm text-muted-foreground">
              {(() => {
                const { page } = result();
                const { totalPages } = result();
                return t(msg`Page ${page} of ${totalPages}`);
              })()}
            </span>
            <Button
              variant="ghost"
              size="sm"
              disabled={result().page >= result().totalPages}
              onClick={() => patchSearch({ page: result().page + 1 })}
            >
              {t(msg`Next`)}
            </Button>
          </div>
        </Show>
      </Show>

      <Dialog
        open={Boolean(dialogUser()) && dialogAction() === "ban"}
        onOpenChange={(open) => {
          if (!open) {
            closeDialog();
          }
        }}
        contentClass="max-w-[420px] p-6"
      >
        <DialogTitle class="text-xl font-bold mb-2">{t(msg`Ban user`)}</DialogTitle>
        <DialogDescription class="text-sm text-muted-foreground mb-4">
          {t(msg`The user will be unable to sign in. You can provide an optional reason.`)}
        </DialogDescription>
        <div class="space-y-2">
          <Label for="ban-reason">{t(msg`Reason (optional)`)}</Label>
          <TextField
            id="ban-reason"
            value={banReason()}
            onInput={(e) => {
              setBanReason(e.currentTarget.value);
            }}
          />
        </div>
        <div class="flex justify-end gap-3 mt-6">
          <Button variant="ghost" onClick={closeDialog}>
            {t(msg`Cancel`)}
          </Button>
          <Button variant="destructive" disabled={banMutation.isPending} onClick={handleBan}>
            {t(msg`Ban user`)}
          </Button>
        </div>
      </Dialog>

      <ConfirmDialog
        open={Boolean(dialogUser()) && dialogAction() === "remove"}
        onOpenChange={(open) => {
          if (!open) {
            closeDialog();
          }
        }}
        title={t(msg`Remove user (GDPR)`)}
        description={t(
          msg`This permanently deletes the user and all their data. This cannot be undone.`,
        )}
        confirmLabel={t(msg`Permanently remove`)}
        variant="destructive"
        isLoading={removeMutation.isPending}
        onConfirm={handleRemove}
        onCancel={closeDialog}
      />
    </div>
  );
}
